import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search, Upload, FileDown, File, X, Camera, ScanLine, Loader2, SkipForward, Check, PenTool, FileUp } from "lucide-react";
import { toast } from "sonner";

const emptyClient = { name: "", surname: "", phone: "", email: "", license_number: "", license_expiry: "", license_issued_by: "", license_issued_date: "", address: "", cap: "", city: "", province: "", fiscal_code: "", vat_number: "", notes: "" };

export default function ClientsPage() {
  const { axiosAuth } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyClient });
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [docDialog, setDocDialog] = useState(null); // client for doc upload
  const [docSignatures, setDocSignatures] = useState([]);
  const [deleteDocDialog, setDeleteDocDialog] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Scanner state
  const SCAN_STEPS = [
    { key: "patente_fronte", label: "Patente - FRONTE" },
    { key: "patente_retro", label: "Patente - RETRO" },
    { key: "ci_fronte", label: "Carta d'Identita - FRONTE" },
    { key: "ci_retro", label: "Carta d'Identita - RETRO" },
    { key: "ts_fronte", label: "Tessera Sanitaria - FRONTE" },
    { key: "ts_retro", label: "Tessera Sanitaria - RETRO" },
  ];
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanStep, setScanStep] = useState(0);
  const [scanImages, setScanImages] = useState({});
  const [scanProcessing, setScanProcessing] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanGenerating, setScanGenerating] = useState(false);
  const scanInputRef = useRef(null);
  // OCR state
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrHighlight, setOcrHighlight] = useState(false);
  const ocrInputRef = useRef(null);
  // Signature state
  const [signatureDialog, setSignatureDialog] = useState(false);
  const [signatureProcessing, setSignatureProcessing] = useState(false);
  const [extractedSignatures, setExtractedSignatures] = useState([]);
  const [signatureSaving, setSignatureSaving] = useState(false);
  const signatureInputRef = useRef(null);

  const fetchClients = useCallback(async () => {
    try { const api = axiosAuth(); const res = await api.get("/clients"); setClients(res.data); }
    catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  useEffect(() => {
    if (!docDialog?.id) { setDocSignatures([]); return; }
    (async () => {
      try {
        const api = axiosAuth();
        const res = await api.get(`/signatures/${docDialog.id}`);
        setDocSignatures(res.data?.signatures || []);
      } catch { setDocSignatures([]); }
    })();
  }, [docDialog?.id, axiosAuth]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const api = axiosAuth();
      if (editing) { await api.put(`/clients/${editing.id}`, form); toast.success("Aggiornato"); }
      else { await api.post("/clients", form); toast.success("Aggiunto"); }
      setDialogOpen(false); setEditing(null); setForm({ ...emptyClient }); fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    try { const api = axiosAuth(); await api.delete(`/clients/${deleteDialog.id}`); toast.success("Eliminato"); setDeleteDialog(null); fetchClients(); }
    catch { toast.error("Errore"); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, surname: c.surname, phone: c.phone || "", email: c.email || "", license_number: c.license_number || "", license_expiry: c.license_expiry || "", license_issued_by: c.license_issued_by || "", license_issued_date: c.license_issued_date || "", address: c.address || "", cap: c.cap || "", city: c.city || "", province: c.province || "", fiscal_code: c.fiscal_code || "", vat_number: c.vat_number || "", notes: c.notes || "" });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !docDialog) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = reader.result.split(",")[1];
        const api = axiosAuth();
        const formData = new FormData();
        formData.append("file_name", file.name);
        formData.append("file_type", file.type);
        formData.append("file_data", b64);
        await api.post(`/clients/${docDialog.id}/documents`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Documento caricato!");
        fetchClients();
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch { toast.error("Errore upload"); setUploading(false); }
  };

  const downloadDoc = async (clientId, docId, fileName) => {
    try {
      const api = axiosAuth();
      const res = await api.get(`/clients/${clientId}/documents/${docId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  const deleteDoc = async (clientId, docId) => {
    try { const api = axiosAuth(); await api.delete(`/clients/${clientId}/documents/${docId}`); toast.success("Documento eliminato"); fetchClients(); }
    catch { toast.error("Errore"); }
  };

  // Scanner functions
  const openScanner = () => { setScannerOpen(true); setScanStep(0); setScanImages({}); setScanPreview(null); };

  const handleScanCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Compress image before sending
    const compressImage = (file, maxWidth = 1200, quality = 0.7) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL("image/jpeg", quality);
          resolve(dataUrl);
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
    const dataUrl = await compressImage(file);
    const b64 = dataUrl.split(",")[1];
    setScanPreview(dataUrl);
    setScanProcessing(true);
    try {
      const api = axiosAuth();
      const res = await api.post("/scanner/process", { image: b64 });
      const step = SCAN_STEPS[scanStep];
      setScanImages(prev => ({ ...prev, [step.key]: res.data.processed_image }));
      toast.success("Documento elaborato!");
    } catch { toast.error("Errore elaborazione. Riprova."); }
    finally { setScanProcessing(false); }
    e.target.value = "";
  };

  const skipScanStep = () => {
    setScanPreview(null);
    if (scanStep < SCAN_STEPS.length - 1) setScanStep(prev => prev + 1);
    else generateScanPdf();
  };

  const nextScanStep = () => {
    setScanPreview(null);
    if (scanStep < SCAN_STEPS.length - 1) setScanStep(prev => prev + 1);
    else generateScanPdf();
  };

  const generateScanPdf = async () => {
    if (Object.keys(scanImages).length === 0) { toast.error("Nessun documento scansionato"); return; }
    setScanGenerating(true);
    try {
      const api = axiosAuth();
      const res = await api.post("/scanner/generate-pdf", { client_id: docDialog.id, images: scanImages }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `Documenti_${docDialog?.surname}_${docDialog?.name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF documenti generato e salvato!");
      setScannerOpen(false);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore generazione PDF"); }
    finally { setScanGenerating(false); }
  };

  // OCR function
  const handleOcr = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result.split(",")[1];
      try {
        const api = axiosAuth();
        const res = await api.post("/ocr/extract", { image: b64, doc_type: "patente" });
        if (res.data.success && res.data.data) {
          const d = res.data.data;
          setForm(prev => ({
            ...prev,
            name: d.nome || prev.name,
            surname: d.cognome || prev.surname,
            address: d.indirizzo || prev.address,
            cap: d.cap || prev.cap,
            city: d.citta || prev.city,
            province: d.provincia || prev.province,
            fiscal_code: d.codice_fiscale || prev.fiscal_code,
            license_number: d.numero_patente || prev.license_number,
            license_expiry: d.data_scadenza_patente || prev.license_expiry,
            license_issued_by: d.ente_rilascio || prev.license_issued_by,
            license_issued_date: d.data_rilascio_patente || prev.license_issued_date,
          }));
          setOcrHighlight(true);
          setTimeout(() => setOcrHighlight(false), 2500);
          toast.success("Dati estratti con successo! Verifica e salva.");
        } else {
          toast.error(res.data.error || "Impossibile leggere il documento");
        }
      } catch (err) { toast.error(err.response?.data?.detail || "Errore OCR"); }
      finally { setOcrLoading(false); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // Signature functions
  const handleSignatureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSignatureProcessing(true);
    setExtractedSignatures([]);
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result.split(",")[1];
      try {
        const api = axiosAuth();
        const res = await api.post("/signatures/extract", { image: b64 });
        setExtractedSignatures(res.data.signatures || []);
        toast.success(`${res.data.count} firme estratte!`);
      } catch (err) { toast.error(err.response?.data?.detail || "Errore estrazione firme"); }
      finally { setSignatureProcessing(false); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const saveSignatures = async () => {
    if (!docDialog || extractedSignatures.length === 0) return;
    setSignatureSaving(true);
    try {
      const api = axiosAuth();
      await api.post("/signatures/save", { client_id: docDialog.id, signatures: extractedSignatures });
      toast.success("Firma salvata nel profilo cliente!");
      setSignatureDialog(false);
      setDocSignatures(extractedSignatures);
      setExtractedSignatures([]);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setSignatureSaving(false); }
  };

  const deleteSignature = async (clientId) => {
    if (!window.confirm("Eliminare la firma salvata?")) return;
    try {
      const api = axiosAuth();
      await api.post("/signatures/save", { client_id: clientId, signatures: [] });
      toast.success("Firma eliminata!");
      fetchClients();
      setDocSignatures([]);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore eliminazione"); }
  };

  const filtered = clients.filter((c) => `${c.name} ${c.surname} ${c.email} ${c.phone}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-white">Gestione Clienti</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{clients.length} clienti</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ ...emptyClient }); setDialogOpen(true); }} data-testid="add-client-button" className="bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-sm">
          <Plus className="w-4 h-4 mr-2" /> Aggiungi Cliente
        </Button>
      </div>

      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
        <CardHeader className="pb-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Cerca..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-600 rounded-lg" data-testid="clients-search-input" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50 dark:bg-slate-800/50">
                <TableHead className="font-medium text-slate-700 dark:text-slate-200">Cliente</TableHead>
                <TableHead className="hidden md:table-cell font-medium text-slate-700 dark:text-slate-200">Telefono</TableHead>
                <TableHead className="hidden lg:table-cell font-medium text-slate-700 dark:text-slate-200">Patente</TableHead>
                <TableHead className="hidden lg:table-cell font-medium text-slate-700 dark:text-slate-200">Documenti</TableHead>
                <TableHead className="text-right font-medium text-slate-700 dark:text-slate-200">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Nessun cliente</TableCell></TableRow>
              ) : filtered.map((c) => (
                <TableRow key={c.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50" data-testid={`client-row-${c.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center">
                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{c.name?.charAt(0)}{c.surname?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{c.name} {c.surname}</p>
                        {c.address && <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate max-w-[180px]">{c.address}{c.city ? `, ${c.city}` : ""}</p>}
                        {!c.address && <p className="text-xs text-slate-500 dark:text-slate-400">{c.email || c.phone || ""}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-slate-600 dark:text-slate-300">{c.phone || "-"}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {c.license_number ? <span className="font-mono text-sm text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">{c.license_number}</span> : "-"}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-1">
                      {(c.documents || []).length > 0 && (
                        <span className="text-xs text-slate-500">{c.documents.length} file</span>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => setDocDialog(c)} className="h-7 w-7 text-slate-500 hover:text-blue-600" data-testid={`docs-client-${c.id}`}>
                        <Upload className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setDocDialog(c)} className="h-8 w-8 text-slate-500 hover:text-blue-600 lg:hidden" data-testid={`docs-mobile-${c.id}`}>
                        <File className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`} className="h-8 w-8 text-slate-500 hover:text-slate-700">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteDialog(c)} data-testid={`delete-client-${c.id}`} className="h-8 w-8 text-slate-500 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-xl" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">{editing ? "Modifica Cliente" : "Nuovo Cliente"}</DialogTitle>
            <DialogDescription>{editing ? "Modifica dati" : "Inserisci dati"}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
            {/* OCR Button */}
            <div className="col-span-2">
              <input ref={ocrInputRef} type="file" accept="image/*" capture="environment" onChange={handleOcr} className="hidden" data-testid="ocr-upload-input" />
              <Button type="button" variant="outline" onClick={() => ocrInputRef.current?.click()} disabled={ocrLoading} className="w-full border-dashed border-2 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 h-10 rounded-lg" data-testid="ocr-button">
                {ocrLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lettura in corso...</> : <><ScanLine className="w-4 h-4 mr-2" /> Compila da Documento (OCR)</>}
              </Button>
            </div>
            <div className="space-y-2"><Label>Nome *</Label><Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`h-10 rounded-lg ${ocrHighlight && form.name ? "ring-2 ring-emerald-400 bg-emerald-50 dark:bg-emerald-950/20" : ""}`} /></div>
            <div className="space-y-2"><Label>Cognome *</Label><Input data-testid="client-surname-input" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className={`h-10 rounded-lg ${ocrHighlight && form.surname ? "ring-2 ring-emerald-400 bg-emerald-50 dark:bg-emerald-950/20" : ""}`} /></div>
            <div className="space-y-2"><Label>Telefono</Label><Input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10 rounded-lg" /></div>
            <div className="space-y-2"><Label>Email</Label><Input data-testid="client-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 rounded-lg" /></div>
            <div className="space-y-2 col-span-2"><Label>Indirizzo</Label><Input data-testid="client-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-10 rounded-lg" placeholder="Via/Piazza..." /></div>
            <div className="space-y-2"><Label>CAP</Label><Input data-testid="client-cap-input" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} className="h-10 rounded-lg" maxLength={5} placeholder="97019" /></div>
            <div className="space-y-2"><Label>Citta</Label><Input data-testid="client-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-10 rounded-lg" placeholder="Vittoria" /></div>
            <div className="space-y-2"><Label>Provincia</Label><Input data-testid="client-province-input" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="h-10 rounded-lg" maxLength={2} placeholder="RG" /></div>
            <div className="space-y-2"><Label>Codice Fiscale</Label><Input data-testid="client-fiscal-input" value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} className="h-10 rounded-lg font-mono" /></div>
            <div className="space-y-2"><Label>P.IVA (azienda)</Label><Input data-testid="client-vat-input" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="h-10 rounded-lg font-mono" placeholder="Solo per aziende" /></div>
            <div className="space-y-2"><Label>N. Patente</Label><Input data-testid="client-license-input" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="h-10 rounded-lg font-mono" /></div>
            <div className="space-y-2"><Label>Rilasciata da</Label><Input data-testid="client-license-issued-by-input" value={form.license_issued_by} onChange={(e) => setForm({ ...form, license_issued_by: e.target.value.toUpperCase() })} className="h-10 rounded-lg" placeholder="es. MIT-UCO" /></div>
            <div className="space-y-2"><Label>Data Rilascio</Label><Input data-testid="client-license-issued-date-input" type="date" value={form.license_issued_date} onChange={(e) => setForm({ ...form, license_issued_date: e.target.value })} className="h-10 rounded-lg" /></div>
            <div className="space-y-2"><Label>Scadenza Patente</Label><Input data-testid="client-license-expiry-input" type="date" value={form.license_expiry} onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} className="h-10 rounded-lg" /></div>
            <div className="space-y-2 col-span-2"><Label>Note</Label><Input data-testid="client-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-10 rounded-lg" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Annulla</Button>
            <Button onClick={handleSave} disabled={submitting || !form.name || !form.surname} data-testid="client-save-button" className="bg-red-600 hover:bg-red-700 text-white rounded-lg">{submitting ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={!!docDialog} onOpenChange={() => setDocDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="documents-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">Documenti - {docDialog?.name} {docDialog?.surname}</DialogTitle>
            <DialogDescription>Carica e gestisci documenti scansionati</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Scanner button */}
            <Button onClick={openScanner} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg" data-testid="scanner-button">
              <Camera className="w-4 h-4 mr-2" /> Scansiona Documenti
            </Button>
            {/* Signature button */}
            <Button onClick={() => { setSignatureDialog(true); setExtractedSignatures([]); }} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg" data-testid="signature-button">
              <PenTool className="w-4 h-4 mr-2" /> {docSignatures.length > 0 ? "Sostituisci Firma" : "Aggiungi Firma Elettronica"}
            </Button>
            {/* Show saved signature */}
            {docSignatures.length > 0 && (
              <div className="p-3 border border-violet-200 dark:border-violet-800 rounded-lg bg-violet-50 dark:bg-violet-950/20" data-testid="saved-signature-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-violet-700 dark:text-violet-400 flex items-center gap-1.5"><PenTool className="w-3 h-3" /> Firma salvata</p>
                  <Button variant="ghost" size="sm" onClick={() => deleteSignature(docDialog.id)} className="h-7 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 rounded" data-testid="delete-signature-button">
                    <Trash2 className="w-3 h-3 mr-1" /> Elimina
                  </Button>
                </div>
                <div className="bg-white dark:bg-slate-800 p-2 rounded flex items-center justify-center" style={{backgroundImage: 'repeating-conic-gradient(#f1f5f9 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px'}}>
                  <img src={`data:image/png;base64,${docSignatures[0]}`} alt="Firma" className="max-h-16 object-contain" />
                </div>
              </div>
            )}
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-red-300 transition-colors">
              <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" id="doc-upload" data-testid="doc-upload-input" />
              <label htmlFor="doc-upload" className="cursor-pointer flex flex-col items-center gap-2">
                <Upload className={`w-6 h-6 ${uploading ? "text-amber-500 animate-pulse" : "text-slate-400"}`} />
                <span className="text-sm text-slate-500">{uploading ? "Caricamento..." : "Clicca per caricare documento"}</span>
                <span className="text-[10px] text-slate-400">PDF, immagini, documenti</span>
              </label>
            </div>
            {(docDialog?.documents || []).length > 0 && (
              <div className="space-y-2">
                {docDialog.documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-2 min-w-0">
                      <File className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="text-sm text-slate-700 truncate">{d.file_name}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => downloadDoc(docDialog.id, d.id, d.file_name)} className="h-7 w-7 text-slate-500 hover:text-blue-600" data-testid={`download-doc-${d.id}`}>
                        <FileDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteDocDialog({ clientId: docDialog.id, docId: d.id, fileName: d.file_name })} className="h-7 w-7 text-slate-500 hover:text-red-600" data-testid={`delete-doc-${d.id}`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-xl"><DialogHeader><DialogTitle>Elimina Cliente</DialogTitle><DialogDescription>Sei sicuro di voler eliminare {deleteDialog?.name} {deleteDialog?.surname}? Questa azione non puo essere annullata.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteDialog(null)} data-testid="cancel-delete-client">Annulla</Button><Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-client">Elimina</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation */}
      <Dialog open={!!deleteDocDialog} onOpenChange={() => setDeleteDocDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle>Elimina Documento</DialogTitle>
            <DialogDescription>Sei sicuro di voler eliminare "{deleteDocDialog?.fileName}"?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDocDialog(null)} data-testid="cancel-delete-doc">Annulla</Button>
            <Button onClick={() => { deleteDoc(deleteDocDialog.clientId, deleteDocDialog.docId); setDocDialog(prev => prev ? {...prev, documents: prev.documents.filter(x => x.id !== deleteDocDialog.docId)} : null); setDeleteDocDialog(null); }} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-doc">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scanner Wizard Dialog */}
      <Dialog open={scannerOpen} onOpenChange={() => { if (!scanProcessing && !scanGenerating) setScannerOpen(false); }}>
        <DialogContent className="sm:max-w-lg rounded-xl" data-testid="scanner-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Scanner Documenti - {docDialog?.name} {docDialog?.surname}
            </DialogTitle>
            <DialogDescription>
              Scansiona i documenti del cliente. Puoi saltare un documento o interrompere la scansione.
            </DialogDescription>
          </DialogHeader>

          {/* Progress bar */}
          <div className="flex items-center gap-1 mb-2">
            {SCAN_STEPS.map((s, i) => (
              <div key={s.key} className={`flex-1 h-1.5 rounded-full transition-colors ${i < scanStep ? "bg-emerald-500" : i === scanStep ? "bg-blue-500" : "bg-slate-200 dark:bg-slate-700"}`} />
            ))}
          </div>
          <p className="text-xs text-slate-400 text-center mb-1">{scanStep + 1} / {SCAN_STEPS.length}</p>

          {scanGenerating ? (
            <div className="py-12 text-center">
              <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-blue-500" />
              <p className="text-sm text-slate-600 dark:text-slate-300">Generazione PDF in corso...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Current step label */}
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">{SCAN_STEPS[scanStep]?.label}</p>
                {scanImages[SCAN_STEPS[scanStep]?.key] && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Scansionato</p>
                )}
              </div>

              {/* Preview or capture */}
              {scanPreview ? (
                <div className="relative">
                  <img src={scanPreview} alt="Anteprima" className="w-full max-h-48 object-contain rounded-lg border border-slate-200" />
                  {scanProcessing && (
                    <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center">
                      <Loader2 className="w-8 h-8 animate-spin text-white" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-blue-200 dark:border-blue-800 rounded-lg p-6 text-center">
                  <input ref={scanInputRef} type="file" accept="image/*" capture="environment" onChange={handleScanCapture} className="hidden" data-testid="scan-capture-input" />
                  <Button onClick={() => scanInputRef.current?.click()} variant="outline" className="border-blue-300 text-blue-600" data-testid="scan-capture-button">
                    <Camera className="w-4 h-4 mr-2" /> Scatta Foto
                  </Button>
                  <p className="text-[10px] text-slate-400 mt-2">Oppure seleziona dalla galleria</p>
                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                    <input id="a4-upload" type="file" accept="image/*" multiple onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (!files.length) return;
                      setScanProcessing(true);
                      const keys = ["patente_fronte", "patente_retro", "ci_fronte", "ci_retro", "ts_fronte", "ts_retro"];
                      for (let i = 0; i < files.length && i < keys.length; i++) {
                        try {
                          const compress = (f) => new Promise((resolve) => {
                            const r = new FileReader();
                            r.onload = (ev) => { const img = new Image(); img.onload = () => {
                              const c = document.createElement("canvas"); let w = img.width, h = img.height;
                              if (w > 1200) { h = (h * 1200) / w; w = 1200; }
                              c.width = w; c.height = h; c.getContext("2d").drawImage(img, 0, 0, w, h);
                              resolve(c.toDataURL("image/jpeg", 0.7).split(",")[1]);
                            }; img.src = ev.target.result; }; r.readAsDataURL(f);
                          });
                          const b64 = await compress(files[i]);
                          const api = axiosAuth();
                          const res = await api.post("/scanner/process", { image: b64 });
                          setScanImages(prev => ({ ...prev, [keys[i]]: res.data.processed_image }));
                        } catch {}
                      }
                      setScanProcessing(false);
                      toast.success(`${files.length} documenti caricati!`);
                      e.target.value = "";
                    }} className="hidden" />
                    <Button onClick={() => document.getElementById("a4-upload")?.click()} variant="ghost" className="text-xs text-slate-500 hover:text-blue-600">
                      <FileUp className="w-3 h-3 mr-1" /> Carica piu' foto insieme
                    </Button>
                    <p className="text-[10px] text-slate-400 mt-1">Seleziona fino a 6 foto (patente, CI, tessera - fronte e retro)</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => { setScannerOpen(false); if (Object.keys(scanImages).length > 0) generateScanPdf(); }} className="flex-1 text-xs rounded-lg" data-testid="scan-skip-all-button">
                  <SkipForward className="w-3 h-3 mr-1" />
                  {Object.keys(scanImages).length > 0 ? "Genera PDF con scansionati" : "Salta tutto (manuale)"}
                </Button>
                <Button variant="outline" onClick={skipScanStep} className="flex-1 text-xs rounded-lg" data-testid="scan-skip-step-button">
                  Salta questo
                </Button>
                {scanImages[SCAN_STEPS[scanStep]?.key] && (
                  <Button onClick={nextScanStep} className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg" data-testid="scan-next-button">
                    {scanStep < SCAN_STEPS.length - 1 ? "Avanti" : "Genera PDF"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={signatureDialog} onOpenChange={() => { if (!signatureProcessing) setSignatureDialog(false); }}>
        <DialogContent className="sm:max-w-lg rounded-xl" data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <PenTool className="w-5 h-5 text-violet-600" /> Firma Elettronica - {docDialog?.name} {docDialog?.surname}
            </DialogTitle>
            <DialogDescription>
              Carica un'immagine con la firma del cliente. La firma verra estratta automaticamente con lo sfondo rimosso e applicata 3 volte nel contratto.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Upload */}
            <input ref={signatureInputRef} type="file" accept="image/*" onChange={handleSignatureUpload} className="hidden" data-testid="signature-upload-input" />
            <Button onClick={() => signatureInputRef.current?.click()} disabled={signatureProcessing} variant="outline" className="w-full border-dashed border-2 border-violet-300 dark:border-violet-700 text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/30 h-12 rounded-lg" data-testid="signature-upload-button">
              {signatureProcessing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Estrazione firma in corso...</> : <><Upload className="w-4 h-4 mr-2" /> Carica Immagine con Firma</>}
            </Button>

            {/* Preview extracted signatures */}
            {extractedSignatures.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Firma estratta (verra applicata 3 volte nel contratto):</p>
                <div className="grid grid-cols-1 gap-3">
                  {extractedSignatures.map((sig, i) => (
                    <div key={i} className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800" data-testid={`signature-preview-${i}`}>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Firma Cliente</p>
                      <div className="bg-slate-50 dark:bg-slate-900 p-2 rounded flex items-center justify-center" style={{backgroundImage: 'repeating-conic-gradient(#f1f5f9 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px'}}>
                        <img src={`data:image/png;base64,${sig}`} alt={`Firma ${i + 1}`} className="max-h-20 object-contain" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSignatureDialog(false)} className="rounded-lg">Annulla</Button>
            {extractedSignatures.length > 0 && (
              <Button onClick={saveSignatures} disabled={signatureSaving} className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg" data-testid="signature-save-button">
                {signatureSaving ? "Salvataggio..." : "Salva Firma"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
